import { Injectable } from '@angular/core';
import {
  ApplicantAppEntityApplicantRead,
  ApplicantService,
  ApplicationAppEntityApplicationRead,
  ApplicationAttachmentService,
  ApplicationService,
  AttachmentAttachmentRead,
  PatchApplicantApplicantItemRequestParams,
  PatchContractorContractorItemRequestParams,
  ContractorService,
  ContractorAppEntityContractorRead,
  ApplicationAppEntityApplicationWrite,
  ApplicationAttachmentAppEntityApplicationRead,
  ServiceAttachmentPositionBaseReadServiceAttachmentPositionItemRead, ApplicantAppEntityApplicantWrite
} from '../openapi';
import { forkJoin, map, mapTo, Observable, of, switchMap } from 'rxjs';
import { dateToPLString } from './helpers';
import { ApplicationListItem, ApplicationPayment, AttachmentPositions } from './application.common.model';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../environments/environment';

export type ApplicationCode = 'roboczy' | 'zalatwiony' | 'wyslany' | 'zatwierdzony';

@Injectable({
  providedIn: 'root'
})
export class ApplicationCommonService {
  constructor(
    private applicationService: ApplicationService,
    private applicantService: ApplicantService,
    private applicationAttachmentService: ApplicationAttachmentService,
    private contractorService: ContractorService,
    private http: HttpClient
  ) {
  }

  public sendApplication(id: string): Observable<any> {
    return this.applicationService.sendApplicationApplicationItem({
      id,
      application: {}
    });
  }

  public approveApplication(payload: ApplicationAppEntityApplicationWrite, attachments: AttachmentAttachmentRead[]): Observable<ApplicationAppEntityApplicationRead> {
    return this.applicationService.postApplicationApplicationCollection({
      application: payload
    }).pipe(
      switchMap((application) => (
          attachments.length ? forkJoin(
            attachments?.map(attachment => this.applicationAttachmentService.postApplicationAttachmentCollectionApplicationAttachmentCollection({
              applicationAttachment: {
                application: application?.uuid,
                attachment: attachment?.id
              }
            }))
          ) : of([])
        ).pipe(
          switchMap(() =>
            this.applicationService.approveApplicationApplicationItem({
              id: application.uuid || '',
              application: {}
            }).pipe(
              mapTo(
                application
              )
            )
          )
        )
      )
    );
  }

  public loadAttachmentPositionsByApplicationId(applicationId: string): Observable<AttachmentPositions> {
    return this.http.get<ServiceAttachmentPositionBaseReadServiceAttachmentPositionItemRead[]>(`${environment.apiBasePath}/service-attachments-positions/by-application-id/${applicationId}?pagination=false`)
      .pipe(
        map(items => items.map(item => {
          return {
            tag: item?.tag || '',
            description: item?.name || '',
            required: !!item?.isRequired,
            multiple: !!item?.isMultiple,
            payment: item?.fee
          };
        }) || [])
      );
  }

  public createApplication(serviceTypeId: string, application?: ApplicationAppEntityApplicationWrite): Observable<ApplicationAppEntityApplicationRead> {
    return this.applicationService.postApplicationApplicationCollection({
      application: {
        ...application,
        service: serviceTypeId
      }
    });
  }

  public updateApplicantData(
    application: ApplicationAppEntityApplicationRead,
    applicationPayload: ApplicationAppEntityApplicationWrite,
    applicantPayload: ApplicantAppEntityApplicantWrite
  ): Observable<ApplicationAppEntityApplicationRead> {
    const deleteRequests = application.applicants?.map(v => this.applicantService.deleteApplicantApplicantItem({
      id: v?.uuid || ''
    }));
    return this.applicantService.postApplicantApplicantCollection({
      applicant: applicantPayload
    }).pipe(
      switchMap((applicant) => (deleteRequests?.length ? forkJoin(deleteRequests) : of([])).pipe(
        switchMap(() => this.applicationService.patchApplicationApplicationItem({
          id: application?.uuid || '',
          application: {
            ...applicationPayload,
            applicants: [applicant?.uuid || '']
          }
        }))
      ))
    );
  }

  public updateContractors(applicationId: string, contractors: PatchContractorContractorItemRequestParams[]): Observable<ApplicationAppEntityApplicationRead> {
    // for existing
    const contractorPatchRequests: Observable<ContractorAppEntityContractorRead>[] = contractors.filter(v => !!v.id)
      .map(item => this.contractorService.patchContractorContractorItem(item));

    // for new when id is empty string
    const contractorPostRequests: Observable<ContractorAppEntityContractorRead>[] = contractors.filter(v => !v.id)
      .map(v => v.contractor).map(contractor => this.contractorService.postContractorContractorCollection({
        contractor
      }));

    return forkJoin([
      ...contractorPatchRequests,
      ...contractorPostRequests
    ]).pipe(
      switchMap((contractors) => this.applicationService.patchApplicationApplicationItem({
        id: applicationId, application: { contractors: contractors.map(contractor => contractor?.uuid || '') }
      }))
    );
  }

  public updateApplicationAttachments(
    application: ApplicationAppEntityApplicationRead, attachments: AttachmentAttachmentRead[]
  ): Observable<any> {
    if (!attachments.length) {
      return this.applicationService.getApplicationItemApplicationItem({ id: application?.uuid || '' });
    }
    const oldIds: string[] = application?.attachments?.map((v: ApplicationAttachmentAppEntityApplicationRead) => v.id || '') || [];
    if (oldIds?.length) {
      return forkJoin(oldIds.map(v => this.applicationAttachmentService.deleteApplicationAttachmentItemApplicationAttachmentItem({
        id: v
      }))).pipe(
        switchMap(() => forkJoin(attachments?.map(attachment => this.applicationAttachmentService.postApplicationAttachmentCollectionApplicationAttachmentCollection({
          applicationAttachment: {
            application: application?.uuid,
            attachment: attachment?.id
          }
        }))).pipe(
          switchMap(() => this.applicationService.getApplicationItemApplicationItem({ id: application?.uuid || '' }))
        ))
      );
    } else {
      return forkJoin(
        attachments?.map(attachment =>
          this.applicationAttachmentService.postApplicationAttachmentCollectionApplicationAttachmentCollection({
            applicationAttachment: {
              application: application?.uuid,
              attachment: attachment?.id
            }
          })
        )
      ).pipe(
        switchMap(() => this.applicationService.getApplicationItemApplicationItem({ id: application?.uuid || '' }))
      );
    }
  }

  public loadApplicationList(code: ApplicationCode): Observable<ApplicationListItem[]> {
    return this.applicationService.getApplicationCollectionApplicationCollection({
      pagination: false,
      statusCode: code,
      orderUpdatedAt: code === 'roboczy' ? 'desc' : undefined,
      orderApprovedAt: code === 'zatwierdzony' ? 'desc' : undefined,
      orderSentAt: code === 'wyslany' || code === 'zalatwiony' ? 'desc' : undefined
    }).pipe(
      map((applications: Array<ApplicationAppEntityApplicationRead>) => applications.filter(v => !!v?.service).map(v => ({
          tag: `${v?.service?.name || ''} ${dateToPLString(v?.createdAt) || ''} ${dateToPLString(v?.updatedAt) || ''} ${dateToPLString(v?.sentAt) || ''} ${dateToPLString(v?.approvedAt) || ''}`,
          application: v
        }))
      )
    );
  }

  public loadApplicationPayments(applicationId: string): Observable<ApplicationPayment> {
    return this.http.post<ApplicationPayment>(`${environment.apiBasePath}/application/${applicationId}/cost-forecast`, {});
  }

  public signApplication(applicationId: string): Observable<{ result: string; redirect_url: string }> {
    return this.http.get<{
      result: string;
      redirect_url: string
    }>(`${environment.apiBasePath}/application/${applicationId}/sign-attachments`);
  }

}
